import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Skeleton } from '../components/ui/skeleton';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  BarChart3,
  Users,
  Heart,
  Package,
  TrendingUp,
  Download,
  Calendar,
  Baby
} from 'lucide-react';
import { toast } from 'sonner';

const StatCard = ({ title, value, icon: Icon, description }) => (
  <Card>
    <CardContent className="p-4 sm:p-6">
      <div className="flex items-start justify-between">
        <div className="min-w-0">
          <p className="text-xs sm:text-sm font-medium text-muted-foreground">{title}</p>
          <p className="text-xl sm:text-3xl font-heading font-bold mt-1 sm:mt-2">{value}</p>
          {description && <p className="text-xs text-muted-foreground mt-0.5">{description}</p>}
        </div>
        <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-primary/10 flex items-center justify-center flex-shrink-0">
          <Icon className="w-5 h-5 sm:w-6 sm:h-6 text-primary" />
        </div>
      </div>
    </CardContent>
  </Card>
);

export const ReportPage = () => {
  const { API } = useAuth();
  const currentYear = new Date().getFullYear();
  const [anno, setAnno] = useState(currentYear.toString());
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);

  const years = Array.from({ length: 5 }, (_, i) => currentYear - i);

  useEffect(() => {
    fetchReport();
  }, [anno]);

  const fetchReport = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/reports/chiesa`, {
        params: { anno: parseInt(anno) }
      });
      setReport(response.data);
    } catch (err) {
      toast.error('Errore nel caricamento del report');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-48" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {[...Array(4)].map((_, i) => (
            <Skeleton key={i} className="h-32" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 animate-fade-in" data-testid="report-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-heading font-bold">Report Annuale</h1>
          <p className="text-xs sm:text-sm text-muted-foreground">Statistiche e metriche dell'attivita del banco alimentare</p>
        </div>
        <Select value={anno} onValueChange={setAnno}>
          <SelectTrigger className="w-full sm:w-32" data-testid="anno-select">
            <Calendar className="w-4 h-4 mr-2" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {years.map((y) => <SelectItem key={y} value={y.toString()}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {/* Main Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-6">
        <StatCard
          title="Famiglie Aiutate"
          value={report?.famiglie_aiutate || 0}
          icon={Users}
        />
        <StatCard
          title="Persone Assistite"
          value={report?.persone_aiutate || 0}
          icon={Heart}
        />
        <StatCard
          title="Bambini Aiutati"
          value={report?.bambini_aiutati || 0}
          icon={Baby}
        />
        <StatCard
          title="Distribuzioni"
          value={report?.distribuzioni_effettuate || 0}
          icon={Package}
        />
      </div>

      {/* Financial Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 sm:gap-6">
        <Card className="lg:col-span-2">
          <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
            <CardTitle className="text-sm sm:text-base">Impatto Economico</CardTitle>
            <CardDescription className="text-xs">Valore degli aiuti distribuiti nell'anno {anno}</CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
              <div>
                <p className="text-xs text-muted-foreground">Valore Aiuti</p>
                <p className="text-2xl sm:text-4xl font-heading font-bold text-primary">
                  {(report?.valore_aiuti_distribuiti || 0).toLocaleString('it-IT', { minimumFractionDigits: 0 })}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Risparmio Famiglie</p>
                <p className="text-2xl sm:text-4xl font-heading font-bold text-green-600">
                  {(report?.risparmio_generato || 0).toLocaleString('it-IT', { minimumFractionDigits: 0 })}
                </p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground">Donazioni Ricevute</p>
                <p className="text-2xl sm:text-4xl font-heading font-bold">
                  {(report?.valore_donazioni_ricevute || 0).toLocaleString('it-IT', { minimumFractionDigits: 0 })}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="px-4 pt-4 sm:px-6 sm:pt-6 pb-2">
            <CardTitle className="text-sm sm:text-base">Top Donatori</CardTitle>
            <CardDescription className="text-xs">Principali sostenitori {anno}</CardDescription>
          </CardHeader>
          <CardContent className="px-4 pb-4 sm:px-6 sm:pb-6">
            {report?.top_donatori?.length > 0 ? (
              <div className="space-y-2 sm:space-y-3">
                {report.top_donatori.slice(0, 5).map((d, i) => (
                  <div key={i} className="flex items-center justify-between">
                    <div className="flex items-center gap-2 min-w-0">
                      <span className="text-xs sm:text-sm font-medium text-muted-foreground">{i + 1}.</span>
                      <span className="font-medium text-sm truncate">{d.nome}</span>
                    </div>
                    <span className="text-xs sm:text-sm font-medium flex-shrink-0">{(d.valore || 0).toFixed(0)}</span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-center text-sm text-muted-foreground py-3">Nessun donatore per questo periodo</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Summary Card */}
      <Card className="bg-gradient-to-br from-primary/5 to-transparent border-primary/20">
        <CardContent className="p-4 sm:p-6 lg:p-8">
          <div className="flex items-start gap-3 sm:gap-4">
            <div className="w-12 h-12 sm:w-16 sm:h-16 rounded-xl sm:rounded-2xl bg-primary flex items-center justify-center flex-shrink-0">
              <TrendingUp className="w-6 h-6 sm:w-8 sm:h-8 text-primary-foreground" />
            </div>
            <div>
              <h3 className="text-base sm:text-xl font-heading font-bold">Riepilogo Anno {anno}</h3>
              <p className="text-xs sm:text-sm text-muted-foreground mt-1 sm:mt-2 leading-relaxed">
                Nel corso dell'anno {anno}, il banco alimentare ha assistito <strong>{report?.famiglie_aiutate || 0} famiglie</strong> per 
                un totale di <strong>{report?.persone_aiutate || 0} persone</strong>, di cui <strong>{report?.bambini_aiutati || 0} bambini</strong>.
                Sono state effettuate <strong>{report?.distribuzioni_effettuate || 0} distribuzioni</strong> per 
                un valore complessivo di <strong>{(report?.valore_aiuti_distribuiti || 0).toLocaleString('it-IT')}</strong>.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default ReportPage;
